import { getConfigVersion } from "../utilities";
import { defaultTagTexture } from "../viewer/textures/tag-icons";
import { AssetType, CameraData } from "./assets-slice";
import { initialAerialState, initialSceneState } from "./camera-slice";
import { currentConfigVersion } from "./project-slice";
import semver from "semver";
import { nanoid } from "@reduxjs/toolkit";
import { ProjectType } from "../types/project";
import { TagSize } from "../types/tags";

const isCameraOrTag = (type) => {
  return (type === AssetType.Tag)
    || (type === AssetType.Planar)
    || (type === AssetType.Panoramic);
};

export const upgradeConfig = (config) => {
  config = upgradeVersion34(config);
  config = upgradeVersion35(config);
  config = upgradeVersion36(config);
  config = upgradeVersion37(config);
  config = upgradeVersion38(config);
  config = upgradeVersion39(config);
  config = upgradeVersion310(config);
  config = upgradeVersion311(config);
  config = upgradeVersion312(config);
  config = upgradeVersion313(config);
  config = upgradeVersion314(config);
  config = upgradeVersion315(config);
  config = upgradeVersion316(config);
  config = upgradeVersion317(config);
  config = upgradeVersion318(config);
  config = upgradeVersion319(config);
  config = upgradeVersion320(config);
  config = upgradeVersion321(config);
  config = upgradeVersion322(config);
  config = upgradeVersion323(config);
  config = upgradeVersion324(config);

  /** Additional upgrades go here */

  // Project config should now be at the latest version
  config.project.configVersion = currentConfigVersion;

  return config;
};

const upgradeVersion324 = (config) => {
  const oldVersion = "3.24.0";
  const newVersion = "3.25.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  // Add anon access to folders
  config.folders = config.folders.map(folder => {
    return {
      ...folder,
      allowAnonViewers: true
    };
  });

  return config;
};

const upgradeVersion323 = (config) => {
  const oldVersion = "3.23.0";
  const newVersion = "3.24.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  // Add model draw distance
  config.settings.modelDistance = 1000;

  return config;
};

const upgradeVersion322 = (config) => {
  const oldVersion = "3.22.0";
  const newVersion = "3.23.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  // Add annotations state and date to each tag item
  config.assets = config.assets.map(asset => {
    if (asset.type === AssetType.Tag) {
      const items = asset.data.items.map(item => {
        delete item.camera;

        return {
          ...item,
          annotations: []
        };
      });

      asset.data = {
        ...asset.data,
        items
      };
    }

    return asset;
  });

  return config;
};

const upgradeVersion321 = (config) => {
  const oldVersion = "3.21.0";
  const newVersion = "3.22.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  // Add "#" to colors in dxf/shp layer data
  config.assets = config.assets.map(asset => {
    if ((asset.type === AssetType.DXF) || (asset.type === AssetType.SHP)) {
      asset.data = asset.data.map(layer => {
        if (layer.color[0] !== "#") {
          layer.color = `#${layer.color}`;
        }

        return layer;
      });
    }

    return asset;
  });

  return config;
};

const upgradeVersion320 = (config) => {
  const oldVersion = "3.20.0";
  const newVersion = "3.21.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  // Add scene state and date to each tag item
  config.assets = config.assets.map(asset => {
    if (asset.type === AssetType.Tag) {
      const items = asset.data.items.map(item => {
        delete item.camera;

        return {
          ...item,
          date: new Date().toISOString(),
          sceneState: null
        };
      });

      asset.data = {
        ...asset.data,
        items
      };
    }

    return asset;
  });

  return config;
};

const upgradeVersion319 = (config) => {
  const oldVersion = "3.19.0";
  const newVersion = "3.20.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  config.custom_links = config.links;
  delete config.links;

  return config;
};

const upgradeVersion318 = (config) => {
  const oldVersion = "3.18.0";
  const newVersion = "3.19.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  // Add tag draw distance
  config.settings.tagDistance = 1000;

  // Update old style camera ids
  const mappedCamerasIDs = {};
  config.assets.forEach(asset => {
    if ((asset.type === AssetType.Panoramic) ||  (asset.type === AssetType.Planar)) {
      const cameras = asset.data as CameraData[];
      cameras.forEach(camera => {
        const oldCameraID = `${asset.id}-${camera.name}`;
        mappedCamerasIDs[oldCameraID] = camera.id;
      });
    }
  });

  if (config.camera.sceneState.camera) {
    try {
      let newCameraID = mappedCamerasIDs[config.camera.sceneState.camera];
      if (newCameraID) {
        config.camera.sceneState.camera = newCameraID;
      }
    } catch {
      console.log("Error updating camera id (camera state)");
    }
  }

  config.bookmarks = config.bookmarks.map(bookmark => {
    bookmark.settings.tagDistance = 1000;

    if (bookmark.sceneState.camera) {
      try {
        let newCameraID = mappedCamerasIDs[bookmark.sceneState.camera];
        if (newCameraID) {
          bookmark.sceneState.camera = newCameraID;
        }
      } catch {
        console.log("Error updating camera id (bookmark)");
      }
    }

    return bookmark;
  });

  // Add size and camera id field for tags
  config.assets = config.assets.map(asset => {
    if (asset.type === AssetType.Tag) {
      const items = asset.data.items.map(item => {
        return {
          ...item,
          camera: null
        };
      });

      asset.data = {
        ...asset.data,
        size: TagSize.Medium,
        items
      };
    }

    return asset;
  });

  return config;
};

const upgradeVersion317 = (config) => {
  const oldVersion = "3.17.0";
  const newVersion = "3.18.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  if ("overrideSettings" in config.settings) {
    delete config.settings.overrideSettings;
  }

  config.assets = config.assets.map(asset => {
    asset.saved = asset.fromJSON;
    asset.cloud = false;
    delete asset.fromJSON;
    return asset;
  });

  return config;
};

const upgradeVersion316 = (config) => {
  const oldVersion = "3.16.0";
  const newVersion = "3.17.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  config.project.type = ProjectType.Standard;

  return config;
};

const upgradeVersion315 = (config) => {
  const oldVersion = "3.15.0";
  const newVersion = "3.16.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  if ("newProject" in config.project) {
    delete config.project.newProject;
  }

  if ("modified" in config.project) {
    delete config.project.modified;
  }

  config.project.uniqueSession = nanoid();
  config.project.id = nanoid();

  return config;
};

const upgradeVersion314 = (config) => {
  const oldVersion = "3.14.0";
  const newVersion = "3.15.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  // Change from folder.isDefault to folder.default
  config.folders = config.folders.map(folder => {
    if (folder.hasOwnProperty('isDefault')) {
      folder.default = folder.isDefault;
      delete folder.isDefault;
    }

    return folder;
  });

  // Move tag texture string from asset.texture to asset.data.texture
  config.assets = config.assets.map(asset => {
    if (asset.type !== AssetType.Tag) return asset;

    asset.data = {
      texture: asset.texture,
      items: asset.data
    };

    delete asset['texture'];
    return asset;
  });

  return config;
};

const upgradeVersion313 = (config) => {
  const oldVersion = "3.13.0";
  const newVersion = "3.14.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  config.assets = config.assets.map(asset => {
    let filePath = asset.data.path;

    if (filePath) {
      asset.path = filePath;
      delete asset.data;
    } else {
      asset.path = null;
    }

    return asset;
  });

  return config;
};

const upgradeVersion312 = (config) => {
  const oldVersion = "3.12.0";
  const newVersion = "3.13.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  config.settings.overrideSettings = true;

  return config;
};

const upgradeVersion311 = (config) => {
  const oldVersion = "3.11.0";
  const newVersion = "3.12.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  // Convert from cloudSize to cloudScale + cloudMinimum
  const cloudSize = config.settings.cloudSize;
  config.settings.cloudScale = Math.min(3.0, cloudSize);
  config.settings.cloudMinimum = 2.0;
  delete config.settings.cloudSize;

  return config;
};

const upgradeVersion310 = (config) => {
  const oldVersion = "3.10.0";
  const newVersion = "3.11.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  config.assets = config.assets.map(asset => {
    // Add unique ID to cameras and tags
    if (isCameraOrTag(asset.type)) {
      asset.data = asset.data.map(tag => {
        return {
          id: nanoid(),
          ...tag
        };
      });
    }

    // Add comments field for tags
    if (asset.type === AssetType.Tag) {
      asset.data = asset.data.map(tag => {
        return {
          ...tag,
          comment: ""
        };
      });
    }

    return asset;
  });

  config.project.configVersion = newVersion;
  return config;
};

const upgradeVersion39 = (config) => {
  const oldVersion = "3.9.0";
  const newVersion = "3.10.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  const extent = config.camera.aerialState.extent;
  const resolution = config.camera.aerialState.resolution;

  // Update camera state
  config.camera.sceneState = {
    ...initialSceneState,
    ...config.camera.sceneState
  };

  // Update aerial state
  config.camera.aerialState = {
    ...initialAerialState,
    ...config.camera.aerialState
  };

  // Convert resolution to zoom
  if (resolution) {
    const zoom = Math.log2(156543.03390625) - Math.log2(resolution);
    config.camera.aerialState.zoom = zoom;
  }

  // Convert extent to center
  if (extent) {
    config.camera.aerialState.center = [
      (extent[0] + extent[2]) / 2,
      (extent[1] + extent[3]) / 2
    ];
  }

  delete config.camera.aerialState.resolution;
  delete config.camera.aerialState.extent;

  config.project.configVersion = newVersion;
  return config;
};

const upgradeVersion38 = (config) => {
  const oldVersion = "3.8.0";
  const newVersion = "3.9.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  // Add default texture for tags
  config.assets = config.assets.map(asset => {
    asset.texture = (asset.type === AssetType.Tag) ? defaultTagTexture : undefined;
    return asset;
  });

  config.project.configVersion = newVersion;
  return config;
};

const upgradeVersion37 = (config) => {
  const oldVersion = "3.7.0";
  const newVersion = "3.8.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  // Apply upgrades
  delete config.settings.orthoOpacity;
  delete config.settings.orthoQuality;
  delete config.settings.controlType;

  config.project.configVersion = newVersion;
  return config;
};

const upgradeVersion36 = (config) => {
  const oldVersion = "3.6.0";
  const newVersion = "3.7.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  // NOTE: Upgrades removed because of refactoring

  config.project.configVersion = newVersion;
  return config;
};

const upgradeVersion35 = (config) => {
  const oldVersion = "3.5.0";
  const newVersion = "3.6.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  // Apply upgrades
  config.settings.cloudMaxPoints = config.settings.cloudMaxPoints/1000000;
  config.settings.cloudDistance = config.settings.cloudDistance * 10;

  config.project.configVersion = newVersion;
  return config;
};

const upgradeVersion34 = (config) => {
  const oldVersion = "3.4.0";
  const newVersion = "3.5.0";

  if (semver.gt(getConfigVersion(config), oldVersion)) {
    return config;
  }

  console.log(`Upgrade configuration file: ${oldVersion} --> ${newVersion}`);

  // NOTE: Upgrades removed because of refactoring

  config.project.configVersion = newVersion;
  return config;
};

